import { Injectable, Inject } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { ColourService } from './colour.service';

// Service Utilitaire pour y mettre tout ce qui pourrait être méthodes standards (conversion date, arrondi math etc...)
// -> Conversion string en XML

@Injectable()
export class UtilService {
    public static readonly FORMAT_DATETIME:string = 'datetime';
    public static readonly FORMAT_DATE:string = 'date';
    public static readonly FORMAT_TIME:string = 'time';

    constructor(
        private _colourService: ColourService,
        @Inject(DOCUMENT) private _document: Document,
        ) { }

        public loadExternalStyle(cssId:string, cssUrl: string) {
            const head = this._document.getElementsByTagName('head')[0];

            let themeLink = this._document.getElementById(cssId) as HTMLLinkElement;
            if (themeLink) {
                themeLink.href = cssUrl;
            } else {
                const style = this._document.createElement('link');
                style.id = cssId;
                style.rel = 'stylesheet';
                style.href = `${cssUrl}`;

                head.appendChild(style);
            }
        }
    /**
     * Convert a number to 2 caracters hexadecimal
     *
     * @param c number Color component in deciaml
     * @returns string Color component in hexadeciaml on 2 caracters
     */
    private colorDec2Hex(c: number): string {
        let hex = c.toString(16);

        return this.lpad(hex, 2, "0");
    }

    /**
     * Convert an RGB/RGBA color into hexadecimal
     *
     * @param R number Red component
     * @param G number Green component
     * @param B number Blue component
     * @param A number Alpha component (optionnal)
     *
     * @returns string Color in hexadecimal
     */
    public convertRGBToHex(R: number, G: number, B: number, A: number = undefined): string {
        return "#" + this.colorDec2Hex(R) + this.colorDec2Hex(G) + this.colorDec2Hex(B) + (A != undefined ? this.colorDec2Hex(A * 255) : '');
    }

    /**
     * Convert an hexadecimal color to decimal value
     *
     * @param hex string Color in hexadecimal
     * @returns number Color in decimal
     */
    public convertColorHexToDecimal(hex: string): number {
        let hexColorName: any = this._colourService.colourNameToHex(hex);

        if (hexColorName != false) {
            hex = hexColorName;
        }
        let hexColor: string = this.forceColorSixHexaDigits(hex);

        let decimal: number = parseInt(hexColor.slice(1), 16);

        return decimal;
    }

    /**
     * Convert a color from RGB to hexxadecimal
     *
     * @params hex string Color in hexadecimal
     *
     * @returns string Color in R,G,B
     */
    public convertColorHexToRgb(hex: string): string {
        let hexByColorName: any = this._colourService.colourNameToHex(hex);

        if (hexByColorName != false) {
            hex = hexByColorName;
        }
        hex = this.forceColorSixHexaDigits(hex);

        if (hex[0] === '#') {
            hex = hex.substr(1);
        }

        const bigint = parseInt(hex, 16);
        const r = (bigint >> 16) & 255;
        const g = (bigint >> 8) & 255;
        const b = bigint & 255;

        return r + "," + g + "," + b;
    }

    /**
     * Force the color to be a six digits hexadecimal color
     * @param hexColor string Color to test and convert
     * @param keepAlpha boolean Keep or not the alpha part of the color
     *
     * @returns string Color in hexadecimal converted if necessary
     */
    public forceColorSixHexaDigits(hexColor: string, keepAlpha: boolean = false): string {
        if (!/^rgba?.*/.test(hexColor.toLocaleLowerCase())) {
            let hexColorByName: any = this._colourService.colourNameToHex(hexColor) + '';

            if (hexColorByName != false && hexColorByName != 'false') {
                hexColor = hexColorByName;
            }
        }

        if (!/^#/.test(hexColor)) {
            hexColor = hexColor.replace(' ', '');
            let rgbMatch: RegExpMatchArray = hexColor.toUpperCase().match(/^RGBA?\(([0-9]+),([0-9]+),([0-9]+),?([0-9\.]+)?\)$/);

            if (rgbMatch != null) {
                hexColor = this.convertRGBToHex(parseInt(rgbMatch[1]), parseInt(rgbMatch[2]), parseInt(rgbMatch[3]), keepAlpha ? parseFloat(rgbMatch[4]) : undefined);
            }
        }

        if (hexColor.length === 4) {
            hexColor = '#' + hexColor[1] + hexColor[1] + hexColor[2] + hexColor[2] + hexColor[3] + hexColor[3];
        }
        hexColor = this.lpad(hexColor, 6, '0');

        return hexColor;
    }

    /**
     * Add or remove lightness to a hexa color
     *
     * @param {colorHex} string couleur en hexa (#66240F)
     * @param {percent} number >0 plus clair, <0 plus foncé
     *
     * @return {colorHex} string couleur en hexa (#66240F)
     */
    public shadeColor(color: string, percent: number): string {
        percent = percent / 100;
        // validate hex string
        color = this.forceColorSixHexaDigits(color);
        color = new String(color).replace(/[^0-9a-f]/gi, '');
        percent = percent || 0;

        // convert to decimal and change luminosity
        let newColor = "#", c, i, black = 0, white = 255;
        for (i = 0; i < 3; i++) {
            c = parseInt(color.substr(i * 2, 2), 16);
            c = Math.round(Math.min(Math.max(black, c + (percent * white)), white)).toString(16);
            newColor += ("00" + c).substr(c.length);
        }
        return newColor;
    }

    public getTodayDate(format?: string): string {
        let now = new Date();
        let year = now.getFullYear();
        let month = ('0' + now.getMonth() + 1).slice(-2);
        let day = ('0' + now.getDate()).slice(-2);

        let today: string;
        if (format === "US") {
            today = month + "/" + day + "/" + year;
        }
        else {
            today = day + "/" + month + "/" + year;
        }
        return today;
    }

    /**
     * @param {Blob} file - File or Blob object or URL
     * @param {string} fileName - file name
     * @param {string} fileExtension - Optionnal file extension
     */
    public saveFileToDisk(file, fileName, extension = '') {
        let url: string = file;
        let fileExtension: string = extension;
        let fileFullName: string = fileName + '.' + fileExtension;
        // Navigateur normaux
        if (!window["ActiveXObject"]) {
            if (typeof (file) !== 'string') {
                url = URL.createObjectURL(file);
                if (!file.type) {
                    file.type = 'video/webm';
                }
                fileExtension = file.type.split('/')[1];
                if (fileName && fileName.indexOf('.') !== -1) {
                    let splitted = fileName.split('.');
                    fileName = splitted[0];
                    fileExtension = splitted[1];
                }
                fileFullName = fileName + '.' + fileExtension;

                // si navigateur IE11 (car IE11 ne supporte pas l'attribut download html5)
                if (typeof navigator.msSaveOrOpenBlob !== 'undefined') {
                    return navigator.msSaveOrOpenBlob(file, fileFullName);
                } else if (typeof navigator.msSaveBlob !== 'undefined') {
                    return navigator.msSaveBlob(file, fileFullName);
                }
            }

            let save = document.createElement('a');
            save.href = url;
            save.target = '_blank';
            save.download = fileFullName;

            let evt = new MouseEvent('click', {
                view: window,
                bubbles: true,
                cancelable: true
            });

            save.dispatchEvent(evt);
        }
        // Version antérieure à Internet Explorer 11 (a tester...)
        else if (!window["ActiveXObject"] && document.execCommand) {
            let _window = window.open(url, '_blank');
            _window.document.close();
            _window.document.execCommand('SaveAs', true, fileName || url);
            _window.close();
        }
    }

    public dataURLToBlob(dataURL): any {
        let BASE64_MARKER = ';base64,';
        if (dataURL.indexOf(BASE64_MARKER) == -1) {
            let parts = dataURL.split(',');
            let contentType = parts[0].split(':')[1];
            let raw = parts[1];

            return new Blob([raw], { type: contentType });
        }

        let parts = dataURL.split(BASE64_MARKER);
        let contentType = parts[0].split(':')[1];
        let raw = window.atob(parts[1]);
        let rawLength = raw.length;

        let uInt8Array = new Uint8Array(rawLength);

        for (let i = 0; i < rawLength; ++i) {
            uInt8Array[i] = raw.charCodeAt(i);
        }

        return new Blob([uInt8Array], { type: contentType });
    }

    public findGetParameter(parameterName: string): string {
        let result = null,
            tmp = [];
        let items = location.search.substr(1).split("&");
        for (let index = 0; index < items.length; index++) {
            tmp = items[index].split("=");
            if (tmp[0] === parameterName) result = decodeURIComponent(tmp[1]);
        }
        return result;
    }

    public lpad(phrase: string, fillLength: number, fillWith: string): string {
        return UtilService.lpad(phrase, fillLength, fillWith);
    }
    
    public static lpad(phrase: string, fillLength: number, fillWith: string): string {
        while (phrase.length < fillLength) {
            phrase = fillWith + phrase;
        }

        return phrase;
    }
    
    public rpad(phrase: string, fillLength: number, fillWith: string): string {
        return UtilService.rpad(phrase, fillLength, fillWith);
    }

    public static rpad(phrase: string, fillLength: number, fillWith: string): string {
        while (phrase.length < fillLength) {
            phrase += fillWith;
        }

        return phrase;
    }
    
    public static formatDateInput(date:Date):string {
        return date != undefined?[UtilService.lpad(String(date.getFullYear()), 4, '0'), UtilService.lpad(String(date.getMonth()+1), 2, '0'), UtilService.lpad(String(date.getDate()), 2, '0')].join('-'):undefined;
    }

    public formatDate(date:Date, formatType:string = UtilService.FORMAT_DATETIME, lang:string = 'fr'):string {
        if(date == undefined) {
            return undefined;
        }

        if(lang == undefined) {
            lang =  navigator.language;
        }

        let format:Intl.DateTimeFormatOptions = { localeMatcher: 'best fit' };

        if([UtilService.FORMAT_DATETIME, UtilService.FORMAT_DATE].indexOf(formatType) >= 0) {
            format.year = 'numeric';
            format.month = '2-digit';
            format.day = '2-digit';
        }

        if([UtilService.FORMAT_DATETIME, UtilService.FORMAT_TIME].indexOf(formatType) >= 0) {
            format.hour = '2-digit';
            format.minute = '2-digit';
            format.second = '2-digit';
        }

        let dateTimeFormat:Intl.DateTimeFormat = new Intl.DateTimeFormat(lang, format);

        return dateTimeFormat.format(date);
    }

   /**
     * Return RGB color components
     * 
     * @param string RGB  rgb(xxx,xxx,xx,xx)
     * 
     * @returns string[]
     */
    public getRgbComponents(color: string) : string[]
    {
        color = color.substring(4, color.length);
        color = color.substring(
            1,
            color.length - 1
        );
        return color.split(",");
    }


}



// WEBPACK FOOTER //
// /home/gitlab-runner/builds/VyQQATNu/0/groupe-dev/nm17/node_modules/nm-socle-application/app/services/util.service.ts